import i18n from "i18next";
import { type FC, useState } from "react";
import { useDispatch } from "react-redux";

import { useCampaignMutation } from "src/app/features/Campaign/services/server/CampaignApi";
import type { CampaignEntity } from "src/app/features/Campaign/types/CampaignEntity";
import { ButtonEvents } from "../../../../../components/Buttons/ButtonEvents";
import { GenericConfirmationPrimaryButton } from "../../../../../components/Buttons/components/GenericConfirmationPrimaryButton";
import type { ButtonProps } from "../../../../../components/FeatureButtons/types";
import { getAssignedDataGroup } from "../../../../../core/router/auth";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import type { ID } from "../../../../../core/types/fe/types";

export const GenerateButton: FC<ButtonProps<CampaignEntity>> = ({ isDisabled, item, items, onReload }) => {
    const dispatch = useDispatch();
    const [request] = useCampaignMutation();
    const [generatedIds, setGeneratedIds] = useState<ID[]>([]);

    const callBackend = () => {
        const notifyOk = () => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("success.campaign.save.successfully"),
                    autoCloseDisabled: false,
                }),
            );
        };

        const groupCode = getAssignedDataGroup()?.code;
        const baseURI = `${ResourceName.DATA_GROUPS}/${groupCode}`;

        request({
            url: `${baseURI}/${ResourceName.CAMPAIGNS}/${item?.id}/generate`,
        })
            .unwrap()
            .then(() => {
                notifyOk();
                onReload?.();
            })
            .catch(() => {
                // error handled in rtkQueryErrorLogger
            });
    };

    const handleGenerate = (campaignId: ID) => {
        if (!generatedIds.includes(campaignId)) {
            setGeneratedIds([...generatedIds, campaignId]);
        }
    };

    const isOnlyOneCreated = items?.length === 1 && item?.state === "CREATED";
    const isAlreadyGenerated = item?.id !== undefined && generatedIds.includes(item.id);

    const isDisabledFinal = isDisabled || !isOnlyOneCreated || isAlreadyGenerated;

    return (
        <GenericConfirmationPrimaryButton
            modalTitle="campaignEditForm.generateCampaign"
            modalBody="campaign.generateModal.text"
            isDisabled={isDisabledFinal}
            translationKey="campaignEditForm.generateCampaign"
            params={{ name: item?.name }}
            item={item || {}}
            onClickCallback={() => {
                callBackend();
                if (item?.id !== undefined) {
                    handleGenerate(item?.id);
                }
                return item || {};
            }}
            onClickEventName={ButtonEvents.ADD}
            name="generate_button"
            className="aevi-button aevi-button-primary"
        />
    );
};
